import {UserType, UserRole, UnitUserRole, BankUserRole, OrgUserRole, PartnerUserRole} from "./types"

export interface RoleInfo {
	displayName: string
	description: string
	adjustedRole?: string
	availableForSelection?: boolean
}

export interface UserRoleMapping {
	[userType: string]: {
		[role: string]: RoleInfo
	}
}

export const USER_ROLE_MAPPING: UserRoleMapping = {
	["orgUser"]: {
		[OrgUserRole.admin]: {
			displayName: "Admin",
			description:
				"Admin users are allowed to execute all the operations that are enabled for the organization, including creating and managing users.",
			adjustedRole: "admin",
			availableForSelection: true,
		},
		[OrgUserRole.orgOperations]: {
			displayName: "Operations",
			description:
				"Operation users are allowed to view all information and make operational decisions such as approve or reject applications",
			adjustedRole: "operations",
			availableForSelection: true,
		},
		[OrgUserRole.restricted]: {
			displayName: "Restricted",
			description:
				"Restricted users are allowed to execute most of the operations that are enabled for the organization. They are not allowed to create users, or to initiate operations that move funds outside of the Unit ecosystem.",
			availableForSelection: true,
		},
		[OrgUserRole.it]: {
			displayName: "IT",
			description: "IT users are allowed to create and manage users for the organization.",
			availableForSelection: false,
		},
		[OrgUserRole.readonly]: {
			displayName: "Readonly",
			description:
				"Readonly users are allowed to see relevant customer information, but are not allowed to make any changes.",
			availableForSelection: true,
		},
		[OrgUserRole.readonlyNoPii]: {
			displayName: "Readonly No PII",
			description: "This role allows read-only access excluding PII and sensitive-data.",
			availableForSelection: true,
		},
		[OrgUserRole.nineAgent]: {
			displayName: "Agent",
			description: "This role allows read-only access excluding PII",
			availableForSelection: false, // Only available for specific orgs via SPECIAL_ROLES_FOR_ORG_IDS
		},
	},
	["unitUser"]: {
		[UnitUserRole.super]: {
			displayName: "Super",
			description: "Unit super user",
			availableForSelection: false, // Disabled for selection
		},
		[UnitUserRole.unitAdmin]: {
			displayName: "Unit Admin",
			description: "Unit admin user",
			adjustedRole: "admin",
			availableForSelection: true,
		},
		[UnitUserRole.compliance]: {
			displayName: "Compliance",
			description: "Unit compliance user",
			availableForSelection: true,
		},
		[UnitUserRole.complianceReadonly]: {
			displayName: "Compliance Readonly",
			description: "Unit compliance readonly user",
			availableForSelection: true,
		},
		[UnitUserRole.unitReadonly]: {
			displayName: "Unit Readonly",
			description: "Unit readonly user",
			adjustedRole: "readonly",
			availableForSelection: true,
		},
		[UnitUserRole.unitSales]: {
			displayName: "Unit Sales",
			description: "Unit sales user",
			adjustedRole: "sales",
			availableForSelection: true,
		},
	},
	["bankUser"]: {
		[BankUserRole.bank]: {
			displayName: "Admin",
			description:
				"Admin users are allowed to manage users (create new users, disable existing users, change passwords); view all information; and export available information. Only two admin users from the bank may be enabled at a given time.",
			adjustedRole: "admin",
			availableForSelection: true,
		},
		[BankUserRole.bankOperations]: {
			displayName: "Operations",
			description:
				"Operations users are allowed to approve or reject payments; view all information; and export available information.",
			adjustedRole: "operations",
			availableForSelection: true,
		},
		[BankUserRole.bankReadonly]: {
			displayName: "Readonly",
			description: "Readonly users are allowed to view all information; and export available information.",
			adjustedRole: "readonly",
			availableForSelection: true,
		},
	},
	["partnerUser"]: {
		[PartnerUserRole.partner]: {
			displayName: "Partner",
			description: "",
			adjustedRole: "admin",
			availableForSelection: true,
		},
	},
}

export const ORG_SPECIFIC_ROLES: {[orgId: string]: {[role: string]: RoleInfo}} = {
	"9": {
		"9_agent": {
			displayName: "Agent",
			description: "This role allows read-only access excluding PII",
			availableForSelection: true, // Available for org 9
		},
	},
}

export const SPECIAL_ROLES_FOR_ORG_IDS: {[key: string]: {[key: string]: string[]}} = {
	"9_agent": {
		prod: [
			"56", // Wix Prod
		],
		sandbox: [
			"45", // Wix Sandbox
		],
	},
}

function getRoleInfo(userType: string, role: string, orgId?: string): RoleInfo | undefined {
	// Check org-specific roles first
	if (userType === "orgUser" && orgId && ORG_SPECIFIC_ROLES[orgId]?.[role]) {
		return ORG_SPECIFIC_ROLES[orgId][role]
	}

	// Check main mapping
	return USER_ROLE_MAPPING[userType]?.[role]
}

export function getRoleDisplayName(userType: string, role: string, orgId?: string): string {
	const roleInfo = getRoleInfo(userType, role, orgId)
	return roleInfo?.displayName || role
}

export function getRoleDescription(userType: string, role: string, orgId?: string): string {
	const roleInfo = getRoleInfo(userType, role, orgId)
	return roleInfo?.description || ""
}

export function getAdjustedRole(userType: string, role: string, orgId?: string): string {
	const roleInfo = getRoleInfo(userType, role, orgId)
	return roleInfo?.adjustedRole || role
}

export function getAvailableRoles(userType: string, orgId?: string): Map<string, string> {
	const roles = new Map<string, string>()

	// Add main roles for the user type
	if (USER_ROLE_MAPPING[userType]) {
		Object.entries(USER_ROLE_MAPPING[userType]).forEach(([role, info]) => {
			// Only include roles that are available for selection
			if (info.availableForSelection !== false) {
				roles.set(role, info.description)
			}
		})
	}

	// Add org-specific roles
	if (userType === "orgUser" && orgId && ORG_SPECIFIC_ROLES[orgId]) {
		Object.entries(ORG_SPECIFIC_ROLES[orgId]).forEach(([role, info]) => {
			// Only include roles that are available for selection
			if (info.availableForSelection !== false) {
				roles.set(role, info.description)
			}
		})
	}

	return roles
}

export function getTypeAndRoleCombined(userType: UserType, userRole: UserRole): string {
	if (userType === UserType.PartnerOnBehalf) {
		return userRole
	}
	if (userType === UserType.System) {
		return "Unit Service"
	}

	const adjustedRole = getAdjustedRole(userType as string, userRole)
	return `${userType} ${adjustedRole} User`
}
